import { jsx } from 'react/jsx-runtime';

/**
 * Renders a list of sub-building options for address selection.
 *
 * @param isExpanded - Whether the sub-building options dropdown is expanded
 * @param selectSubBuilding - Callback function invoked when a sub-building option is selected
 * @param address - The parent address object containing sub-building options
 *
 * @returns A `<ul>` element containing selectable sub-building options, or `null` if no address is provided or the dropdown is not expanded
 *
 * @example
 * ```tsx
 * <SubBuildingOptions
 *   isExpanded={true}
 *   selectSubBuilding={(option) => console.log(option)}
 *   address={addressData}
 * />
 * ```
 */
const styles = {
    subBuildingOption: 'py-2 pl-6 hover:bg-primary-100 cursor-pointer text-sm',
};
const SubBuildingOptions = ({ isExpanded, selectSubBuilding, address, }) => {
    if (!address) {
        return null;
    }
    const selectOption = (result, subAddress) => {
        const fullAddress = [
            [result.addressLine1, subAddress.sub].filter(Boolean).join(' '),
            result.city,
            [result.regionOrStateCode, result.postalCode].filter(Boolean).join(' '),
        ]
            .filter(Boolean)
            .join(', ');
        const selectedOption = {
            id: subAddress.id,
            addressLine1: result.addressLine1,
            address: fullAddress,
            street: result.street,
            streetNumber: result.streetNumber,
            city: result.city,
            regionOrStateCode: result.regionOrStateCode,
            postalCode: result.postalCode,
            subBuilding: subAddress.sub,
        };
        selectSubBuilding(selectedOption);
    };
    return isExpanded && Array.isArray(address.subBuilding) ? (jsx("ul", { children: address.subBuilding.map((subAddress) => (jsx("li", { role: "option", tabIndex: 0, className: styles['subBuildingOption'], onClick: () => selectOption(address, subAddress), onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectOption(address, subAddress);
                }
            }, children: subAddress.sub }, subAddress.id))) })) : null;
};

export { SubBuildingOptions };
